// --- START OF FILE components/ProjectCard.js ---
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, User, ChevronDown, TrendingUp, HardDrive, Slack } from 'lucide-react';
import ProjectLinkButton from './ProjectLinkButton';
import SlackChannelDialog from './SlackChannelDialog';
import { DEFAULT_PROJECT_PRIORITY_VALUE, getProjectPriorityInfo } from '../utils/projectPriority';

// Helper function for status styling
const getStatusClasses = (status) => {
  switch (status) {
    case 'proposition': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'completed': return 'bg-green-100 text-green-800 border-green-300';
    default: return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

// Helper function pour formater le CA (Chiffre d'Affaires)
const formatTurnover = (amount) => {
  if (amount == null || isNaN(amount)) return 'N/A';
  // Format en euros, sans décimales
  return amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const statusTranslations = {
  proposition: "Proposition",
  in_progress: "En Cours",
  completed: "Terminé"
};

const availableStatuses = [
  { value: 'proposition', label: statusTranslations.proposition },
  { value: 'in_progress', label: statusTranslations.in_progress },
  { value: 'completed', label: statusTranslations.completed },
];

const ProjectCard = ({ project, onStatusUpdate, onLinkUpdate, calculatedTurnover = 0, apiBaseUrl, circles = [] }) => {
  const navigate = useNavigate();
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [imageError, setImageError] = useState(false);

  const handleCardClick = (e) => {
    // Empêche la navigation si on clique sur le sélecteur de statut ou les liens
    if (e.target.closest('.status-select-wrapper') || e.target.closest('.project-links-wrapper')) {
      return;
    }
    navigate(`/project/${project.id}`);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus !== project.status && onStatusUpdate) {
      onStatusUpdate(project.id, newStatus);
    }
  };

  const [slackDialog, setSlackDialog] = useState(false);

  const handleLinkEdit = (field, label) => {
    if (!onLinkUpdate) return;
    const currentValue = project?.[field] || '';
    const newValue = window.prompt(`Lien ${label}`, currentValue);
    if (newValue === null) return;
    const trimmed = newValue.trim();
    onLinkUpdate(project.id, field, trimmed === '' ? null : trimmed);
  };

  const statusClasses = getStatusClasses(project.status);
  const projectPriorityInfo = getProjectPriorityInfo(project?.pilotage_priority, DEFAULT_PROJECT_PRIORITY_VALUE);

  // Generate initials for fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const heroImageSrc = useMemo(() => {
    if (!project?.hero_image_url) return null;
    if (project.hero_image_url.startsWith("http"))
      return project.hero_image_url;
    if (project.hero_image_url.startsWith("/")) {
      // Ensure apiBaseUrl doesn't have a trailing slash if the path has a leading slash, or handle it gracefully
      // But typically apiBaseUrl is like "http://localhost:8000" or "http://localhost:8000/api"
      // If apiBaseUrl is missing, we might fallback to just the path, but that's risky if it's on a different port.
      // Assuming apiBaseUrl is passed correctly.
      return `${apiBaseUrl || ''}${project.hero_image_url}`;
    }
    return project.hero_image_url;
  }, [project?.hero_image_url, apiBaseUrl]);

  useEffect(() => {
    setImageError(false);
  }, [heroImageSrc]);

  const imageLoaded = Boolean(heroImageSrc && loadedSrc === heroImageSrc);

  const imagePlaceholderGradient = useMemo(() => {
    const seed = `${project?.id ?? ''}-${project?.name ?? ''}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    const safeHash = Math.abs(hash);
    const hue = safeHash % 360;
    const hue2 = (hue + 35 + (safeHash % 25)) % 360;
    const saturation = 55 + (safeHash % 15);
    const light1 = 64 + (safeHash % 10);
    const light2 = 50 + (safeHash % 12);
    return `linear-gradient(135deg, hsl(${hue}, ${saturation}%, ${light1}%), hsl(${hue2}, ${saturation}%, ${light2}%))`;
  }, [project?.id, project?.name]);

  const circleName = useMemo(() => {
    if (!project?.circle_id) return null;
    const match = (circles || []).find((circle) => String(circle.id) === String(project.circle_id));
    return match?.name || null;
  }, [circles, project?.circle_id]);

  return (
    <div
      className="group bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-300 ease-in-out cursor-pointer overflow-hidden flex flex-row sm:flex-col h-full"
      onClick={handleCardClick}
    >
      {/* Image Section */}
      <div className="relative w-20 shrink-0 self-stretch overflow-hidden bg-gray-100 sm:h-40 sm:w-full sm:self-auto">
        {heroImageSrc && !imageError ? (
          <>
            <div
              className={`absolute inset-0 transition-opacity duration-500 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}
              style={{
                backgroundImage: imagePlaceholderGradient,
                filter: 'blur(10px)',
                transform: 'scale(1.08)'
              }}
            />
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}
            >
              <span className="text-3xl font-bold text-white/30 select-none">
                {getInitials(project.name)}
              </span>
            </div>
            <img
              src={heroImageSrc}
              alt={project.name}
              loading="lazy"
              decoding="async"
              onLoad={() => setLoadedSrc(heroImageSrc)}
              onError={() => setImageError(true)}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
            />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <span className="text-3xl font-bold text-white opacity-20 select-none">
              {getInitials(project.name)}
            </span>
          </div>
        )}

        {/* Status Badge Overlay */}
        <div className="absolute top-3 right-3 hidden status-select-wrapper sm:block" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <select
              value={project.status || ''}
              onChange={handleStatusChange}
              className={`pl-3 pr-8 py-1.5 rounded-full text-xs font-semibold border shadow-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-white/50 backdrop-blur-md
                  ${statusClasses} bg-opacity-90 border-opacity-50`}
              title="Changer le statut"
            >
              {availableStatuses.map(statusOption => (
                <option key={statusOption.value} value={statusOption.value}>
                  {statusOption.label}
                </option>
              ))}
              {!availableStatuses.some(s => s.value === project.status) && project.status && (
                <option value={project.status} disabled>{project.status}</option>
              )}
            </select>
            <ChevronDown className={`absolute right-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 pointer-events-none opacity-70`} />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="min-w-0 p-3 sm:p-5 flex-grow flex flex-col">

        {/* Header */}
        <div className="mb-1.5 sm:mb-4">
          <div className="flex justify-between items-start gap-2 mb-1">
            <h3 className="min-w-0 flex-1 text-base sm:text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors" title={project.name}>
              {project.name || 'Projet sans nom'}
            </h3>
            {projectPriorityInfo && (
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${projectPriorityInfo.className}`}
                title={`Priorite projet : P${projectPriorityInfo.value} - ${projectPriorityInfo.label}`}
              >
                P{projectPriorityInfo.value}
              </span>
            )}
          </div>
          {project.client_name && (
            <div className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <Briefcase className="w-3 h-3 mr-1.5" />
              <span className="truncate" title={project.client_name}>{project.client_name}</span>
            </div>
          )}
        </div>

        {/* Vision */}
        <p className="hidden sm:block text-sm text-gray-600 mb-6 line-clamp-2 flex-grow" title={project.vision || 'Pas de vision définie'}>
          {project.vision || <span className="italic text-gray-400">Pas de vision définie</span>}
        </p>

        {/* Footer Info */}
        <div className="sm:pt-4 sm:border-t border-gray-100 mt-auto space-y-3">

          {/* Stats Row */}
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className={`sm:hidden truncate rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusClasses}`}>
              {availableStatuses.find((s) => s.value === project.status)?.label || project.status || '—'}
            </span>
            <div className="hidden sm:flex items-center text-gray-700" title="Cercle">
              <User className="w-4 h-4 mr-2 text-gray-400" />
              <span className="font-medium truncate max-w-[100px]">
                {circleName || <span className="text-gray-400 font-normal">N/A</span>}
              </span>
            </div>
            <div className="flex items-center text-gray-900 font-semibold bg-gray-50 px-2 py-1 rounded-md border border-gray-200">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5 text-green-600" />
              {formatTurnover(calculatedTurnover)}
            </div>
          </div>

          {/* Links Row */}
          <div className="project-links-wrapper hidden sm:flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            <ProjectLinkButton
              icon={HardDrive}
              label="Drive"
              url={project?.google_drive_url}
              onEdit={() => handleLinkEdit('google_drive_url', 'Google Drive')}
              size="sm"
            />
            <ProjectLinkButton
              icon={Slack}
              label="Slack"
              url={project?.slack_channel_url}
              onEdit={() => handleLinkEdit('slack_channel_url', 'Slack')}
              onEmpty={() => setSlackDialog(true)}
              size="sm"
            />
            <SlackChannelDialog
              projectId={project.id}
              open={slackDialog}
              onClose={() => setSlackDialog(false)}
              onCreated={(link) => onLinkUpdate?.(project.id, 'slack_channel_url', link)}
              onLinkExisting={() => handleLinkEdit('slack_channel_url', 'Slack')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
// --- END OF FILE components/ProjectCard.js ---
